import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve session ID for view tracking
function getSessionId(): string {
  const key = "help_article_session_id";
  let sessionId = sessionStorage.getItem(key);
  
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  
  return sessionId;
}

// Track which articles have been viewed this session (client-side deduplication)
function getViewedArticles(): Set<string> {
  const key = "help_articles_viewed";
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function markArticleViewed(articleId: string): void {
  const key = "help_articles_viewed";
  const viewed = getViewedArticles();
  viewed.add(articleId);
  sessionStorage.setItem(key, JSON.stringify([...viewed]));
}

export function useArticleViewTracking(articleId: string | undefined) {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!articleId || hasTracked.current) return;

    // Client-side deduplication - don't re-track in same session
    const viewedArticles = getViewedArticles();
    if (viewedArticles.has(articleId)) {
      return;
    }

    const trackView = async () => {
      try {
        const sessionId = getSessionId();
        
        const { error } = await supabase.functions.invoke("track-article-view", {
          body: {
            article_id: articleId,
            session_id: sessionId,
          },
        });

        if (error) {
          console.error("Failed to track article view:", error);
        } else {
          markArticleViewed(articleId);
          hasTracked.current = true;
        }
      } catch (err) {
        console.error("Error tracking article view:", err);
      }
    };

    // Small delay to ensure page has loaded
    const timeout = setTimeout(trackView, 500);
    return () => clearTimeout(timeout);
  }, [articleId]);
}
